import {IPartner} from 'types/partners';
import {ITransaction} from 'types/diplomat/transaction/Transaction';
import React from 'react';
import {Grid, Typography} from '@mui/material';
import Dialog from 'components/Dialog';
import AsyncContainer from 'components/AsyncContainer';
import Panel from 'components/Panel';
import {useTranslation} from 'react-i18next';
import Button from 'components/Button';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {useSelector} from 'react-redux';
import {default as transactionsSelectors} from 'redux/transactions/selectors';
import {default as partnersSelectors} from 'redux/partners/selectors';
import ReferencesAlert from 'components/ReferencesAlert';
import PassphraseField from 'components/inputs/PassphraseField';
import {ConfirmationDialog} from 'components/dialogs/ConfirmationDialog';
import {IDiplomatObject} from 'types/DiplomatObject';

type DeleteKeyDialogProps = {
   dialogTitle: string;
   data: INav;
   partners: IPartner[];
   transactions: ITransaction[];
   synchronizations?: IDiplomatObject[];
   sftpServers?: IDiplomatObject[];
   requirePassphrase?: boolean;
   forceDeletionPrompt?: boolean;
   onConfirm: (values?: {passphrase: string; force: boolean}) => void;
   onClose: () => void;
   onClear?: () => void;
};

const DeleteKeyDialog = ({
   dialogTitle,
   data,
   partners,
   transactions,
   synchronizations,
   sftpServers,
   requirePassphrase = false,
   forceDeletionPrompt = false,
   onConfirm,
   onClose,
   onClear,
}: DeleteKeyDialogProps) => {
   const {t} = useTranslation();
   const [passphrase, setPassphrase] = React.useState('');
   const [ignoreReferences, setIgnoreReferences] = React.useState(false);
   const isTransactionsFetching = useSelector(transactionsSelectors.isFetching);
   const isPartnersFetching = useSelector(partnersSelectors.isFetching);
   const handleIgnoreReferences = () => {
      setIgnoreReferences(true);
   };
   const handleDelete = () => {
      onConfirm(requirePassphrase ? {passphrase, force: false} : undefined);
   };
   const handleForceDelete = () => {
      onConfirm({passphrase, force: true});
   };

   const hasReferences =
      partners.length > 0 ||
      transactions.length > 0 ||
      synchronizations?.length ||
      sftpServers?.length;
   const objectName =
      data.objectType === DiplomatObjectType.enum.SSL_CLIENT_CERTIFICATE ||
      data.objectType === DiplomatObjectType.enum.SSL_CERTIFICATE
         ? t('Certificate')
         : t('Key');
   const shouldPrompt = Boolean(hasReferences && !ignoreReferences);
   return (
      <Dialog maxWidth="sm" header={dialogTitle} close={onClose}>
         <AsyncContainer loading={isTransactionsFetching || isPartnersFetching}>
            <Panel root>
               {shouldPrompt ? (
                  <ReferencesAlert
                     data={data}
                     objectName={objectName}
                     partners={partners}
                     transactions={transactions}
                     synchronizations={synchronizations}
                     sftpServers={sftpServers}
                     clearAvailable={Boolean(onClear)}
                  />
               ) : (
                  <>
                     <Typography>
                        {t('Do you really want to delete {{objectName}} "{{name}}"?', {
                           objectName,
                           name: data.name,
                        })}
                     </Typography>
                     {requirePassphrase && (
                        <>
                           <PassphraseField
                              autoFocus
                              name="passphrase"
                              placeholder={t('Private Key Passphrase:')}
                              value={passphrase}
                              onChange={(event) => setPassphrase(event.target.value)}
                           />
                           <ConfirmationDialog
                              open={forceDeletionPrompt}
                              title={t('Invalid Key Passphrase')}
                              content={t(
                                 'Passphrase is invalid. Do you want to force deletion of the Key'
                              )}
                              confirmLabel={t('Yes')}
                              cancelLabel={t('No')}
                              onOk={handleForceDelete}
                              onCancel={onClose}
                           />
                        </>
                     )}
                  </>
               )}
            </Panel>
            <Grid container spacing={2} justifyContent={'center'}>
               <Grid item>
                  <PrimaryButton onClick={shouldPrompt ? handleIgnoreReferences : handleDelete}>
                     {t('Delete')}
                  </PrimaryButton>
               </Grid>
               {shouldPrompt && onClear && (
                  <Grid item>
                     <Button onClick={onClear}>{t('Clear')}</Button>
                  </Grid>
               )}
               <Grid item>
                  <Button onClick={onClose}>{t('Cancel')}</Button>
               </Grid>
            </Grid>
         </AsyncContainer>
      </Dialog>
   );
};

export default DeleteKeyDialog;
